import React, { useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Copy, X, Loader2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Switch } from '../../components/ui/switch';
import CustomButton from '../../components/Button/Button';
import UserContext from '../../context/UserContext/UserContext';
import AnalyticsService from '../../services/AnalyticsService/AnalyticsService';
import IndividualConversation from '../../types/IndividualConversation';
import Issue from '../../types/Issue';
import ChatMessageContainer from './ChatMessageContainer/ChatMessageContainer';
import ChatAnalysisContainer from './ChatAnalysisContainer/ChatAnalysisContainer';
import ChatbotContext from '../../context/ChatbotContext/ChatbotContext';
import useLocalStorage from '../../hooks/useLocalStorage';
import useAuthRoles from '../../hooks/useAuthRoles';

interface ConversationSidebarProps extends React.PropsWithChildren<{}> {
  chatId?: number | null;
  previousUrl?: string;
  onClose?: () => void;
  onReviewedCountChange?: (chatId: number, count: number) => void;
}

function countReviewedMessages(
  conversation: IndividualConversation | null
): number {
  if (!conversation) return 0;
  return conversation.messageHistory.filter(
    (msg) =>
      msg.chatMessage.dashboardRating != null || msg.chatMessage.reviewTag
  ).length;
}

// Errors raised within the sidebar are not displayed.
const ignoreError = (): void => {};

function ConversationSidebar({
  chatId: propChatId,
  previousUrl,
  onClose,
  onReviewedCountChange
}: Readonly<ConversationSidebarProps>): React.ReactElement {
  const params = useParams();
  const chatId = propChatId ?? (params.chatId ? Number(params.chatId) : null);

  const [conversation, setConversation] =
    React.useState<IndividualConversation | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const navigate = useNavigate();
  const { user } = React.useContext(UserContext);
  const { currentChatbot } = React.useContext(ChatbotContext);
  const { userIsJapetoAdmin } = useAuthRoles();

  const [selectedTab, setSelectedTab] = React.useState('conversation');
  const [hideReviews, setHideReviews] = useLocalStorage<boolean>(
    'japeto-hide-reviews',
    false
  );

  const handleCloseSidebar = (): void => {
    if (onClose) {
      onClose();
    } else {
      navigate(previousUrl || '/');
    }
  };

  useEffect(() => {
    setIsLoading(true);
    const controller = new AbortController();
    if (user && chatId && currentChatbot) {
      AnalyticsService.getIndividualConversation(
        currentChatbot.id,
        String(chatId),
        controller.signal
      )
        .then((data) => setConversation(data))
        .catch(ignoreError)
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
    return () => controller.abort();
  }, [user, chatId, currentChatbot]);

  const handleMessageReviewUpdate = useCallback(
    (
      messageId: number,
      update: {
        rating?: 'GOOD' | 'OK' | 'BAD' | null;
        tag?: { id: number; name: string; description: string } | null;
        comment?: string | null;
        issue?: Issue | null;
      }
    ) => {
      setConversation((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          messageHistory: prev.messageHistory.map((msg) =>
            msg.messageId === messageId
              ? {
                  ...msg,
                  chatMessage: {
                    ...msg.chatMessage,
                    ...(update.rating !== undefined && {
                      dashboardRating: update.rating
                    }),
                    ...(update.tag !== undefined && { reviewTag: update.tag }),
                    ...(update.comment !== undefined && {
                      reviewComment: update.comment
                    }),
                    ...(update.issue !== undefined && { issue: update.issue })
                  }
                }
              : msg
          )
        };
        if (chatId != null && onReviewedCountChange) {
          onReviewedCountChange(chatId, countReviewedMessages(next));
        }
        return next;
      });
    },
    [chatId, onReviewedCountChange]
  );

  const renderContent = (): React.ReactNode => {
    if (selectedTab === 'conversation') {
      if (isLoading) {
        return (
          <div className='flex justify-center h-[200px] items-center'>
            <Loader2 className='h-20 w-20 animate-spin text-primary' />
          </div>
        );
      }
      if (conversation) {
        return (
          <ChatMessageContainer
            conversation={conversation}
            setError={ignoreError}
            hideReviews={hideReviews}
            onMessageReviewUpdate={handleMessageReviewUpdate}
          />
        );
      }
      return null;
    }

    if (selectedTab === 'analytics' && conversation) {
      return <ChatAnalysisContainer conversation={conversation} />;
    }

    if (selectedTab === 'actions') {
      return (
        <div className='max-w-lg'>
          <div className='border border-black/20 rounded-lg bg-white px-6 py-4'>
            <h6 className='text-xl font-medium normal-case mb-0.5'>
              Conversation Actions
            </h6>
            <p className='mb-4 text-sm text-black/60'>
              Manage this conversation
            </p>
            <div className='flex flex-col gap-4'>
              <CustomButton
                variant='outline'
                icon={<Copy size='20' />}
                onClick={() => {
                  if (chatId) {
                    navigator.clipboard
                      .writeText(String(chatId))
                      .catch(ignoreError);
                  }
                }}
              >
                Copy conversation ID
              </CustomButton>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <Helmet>
        <title>Japeto - Chat Analysis</title>
      </Helmet>
      <div className='p-4 overflow-hidden h-screen'>
        <div className='flex items-center justify-between mb-4 w-full'>
          <h6 className='text-xl font-normal'>Conversation Details</h6>
          <div className='flex items-center gap-3'>
            {userIsJapetoAdmin() && (
              <div className='flex items-center gap-1.5'>
                <Switch
                  checked={hideReviews}
                  onCheckedChange={setHideReviews}
                />
                <span className='text-xs text-gray-500'>Hide reviews</span>
              </div>
            )}
            <Button
              variant='ghost'
              size='icon'
              onClick={handleCloseSidebar}
              className='text-gray-400 hover:text-gray-600 hover:bg-transparent -mr-2'
            >
              <X className='h-5 w-5' />
            </Button>
          </div>
        </div>
        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <TabsList className='bg-tabs-bg rounded-md p-1 w-full grid grid-cols-3'>
            <TabsTrigger
              value='conversation'
              className='font-normal rounded-md px-2 py-1.5 z-10 normal-case
                data-[state=active]:bg-white data-[state=active]:text-black
                data-[state=inactive]:text-gray-500'
            >
              Conversation
            </TabsTrigger>
            <TabsTrigger
              value='analytics'
              className='font-normal rounded-md px-2 py-1.5 z-10 normal-case
                data-[state=active]:bg-white data-[state=active]:text-black
                data-[state=inactive]:text-gray-500'
            >
              Analytics
            </TabsTrigger>
            <TabsTrigger
              value='actions'
              className='font-normal rounded-md px-2 py-1.5 z-10 normal-case
                data-[state=active]:bg-white data-[state=active]:text-black
                data-[state=inactive]:text-gray-500'
            >
              Actions
            </TabsTrigger>
          </TabsList>
          <TabsContent value={selectedTab} className='mt-0'>
            <ScrollArea className='mt-2 h-[calc(100vh-145px)] overflow-x-hidden'>
              {renderContent()}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

export default ConversationSidebar;
